import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { BankOption } from '../lib/tests';
import type { AnswerState } from './useSitting';
import { displayLetter, isRomanBank } from './letters';

/**
 * Drag and drop, as the real CD IELTS screen does it.
 *
 * Two tasks work this way: the summary with a word bank, where cards are
 * dropped into blanks in the text, and matching headings, where headings are
 * dropped onto the paragraphs of the passage — which sits in the *other* pane.
 * So the picked-up card cannot be state inside one group: it is held here, in
 * context, and both panes read it.
 *
 * Dragging is not the only way to answer. A card can be clicked and then a
 * blank clicked, which is what a candidate on a touch screen or a keyboard
 * has, and dropping is never the only route to a mark.
 */

export interface DragPick {
  groupId: string;
  letter: string;
}

interface DragState {
  pick: DragPick | null;
  setPick: (pick: DragPick | null) => void;
}

const DragContext = createContext<DragState>({ pick: null, setPick: () => undefined });

export function DragProvider({ children }: { children: ReactNode }) {
  const [pick, setPick] = useState<DragPick | null>(null);
  const value = useMemo(() => ({ pick, setPick }), [pick]);
  return <DragContext.Provider value={value}>{children}</DragContext.Provider>;
}

export const useDragPick = () => useContext(DragContext);

const MIME = 'text/plain';

/** The cards a candidate drags from: phrases for a summary, headings for a passage. */
export function BankPanel({
  groupId,
  title,
  hint,
  options,
  used,
  oneUse,
  disabled,
  layout,
  showLetters,
}: {
  groupId: string;
  title?: string;
  hint: string;
  options: BankOption[];
  used: Set<string>;
  /**
   * True where a card, once placed, is gone from the bank — headings, endings,
   * a summary's phrases. False where the rubric allows a letter to answer more
   * than one question, in which case a used card stays, dimmed.
   */
  oneUse: boolean;
  disabled: boolean;
  /**
   * `rows` stacks full-width cards (headings), `chips` flows them (phrases),
   * and `plain` is the column of unlettered cards a Listening paper puts
   * beside its gaps — the answers are the words themselves there, and the
   * letters they are stored under are the marker's business, not the
   * candidate's.
   */
  layout: 'rows' | 'chips' | 'plain';
  /**
   * Whether each card shows its letter.
   *
   * Normally it follows the layout — a Listening paper's cards are plain,
   * because there the candidate drags a word into a numbered gap and the
   * letter is bookkeeping. But a rubric that says "write the correct letter,
   * A-F" is asking for the letter itself, and a card with no letter on it
   * cannot answer that question. So the group decides, not the layout.
   */
  showLetters?: boolean;
}) {
  const { pick, setPick } = useDragPick();
  const roman = isRomanBank(options);

  // A card that has been placed is taken out of the bank rather than left
  // greyed behind: the real screen leaves nothing where it stood, and a ghost
  // of an answer already given is one more thing to read past.
  const isUsed = (option: BankOption) => used.has(option.letter.toUpperCase());
  const shown = oneUse ? options.filter((option) => !isUsed(option)) : options;

  return (
    <div className={`dbank${layout === 'plain' ? ' dbank--plain' : ''}`}>
      {title && <h4 className="dbank__title">{title}</h4>}
      {hint && <p className="dbank__hint">{hint}</p>}
      <ul className={`dbank__list dbank__list--${layout}`}>
        {shown.map((option) => {
          const picked = pick?.groupId === groupId && pick.letter === option.letter;
          return (
            <li key={option.letter}>
              <div
                className="dbank__item"
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-pressed={picked}
                aria-disabled={disabled || undefined}
                data-used={isUsed(option) || undefined}
                draggable={!disabled}
                onDragStart={(e) => {
                  e.dataTransfer.setData(MIME, option.letter);
                  e.dataTransfer.effectAllowed = 'copyMove';
                  setPick({ groupId, letter: option.letter });
                }}
                onDragEnd={() => setPick(null)}
                onClick={() => {
                  if (disabled) return;
                  setPick(picked ? null : { groupId, letter: option.letter });
                }}
                onKeyDown={(e) => {
                  if (disabled) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPick(picked ? null : { groupId, letter: option.letter });
                  }
                }}
              >
                {(showLetters ?? layout !== 'plain') && (
                  <span className="dbank__letter">{displayLetter(option.letter, roman)}.</span>
                )}
                <span className="dbank__text">{option.text}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** A blank a card is dropped into — in a summary, or above a paragraph. */
export function DropZone({
  groupId,
  number,
  answer,
  option,
  roman = false,
  placeholder,
  block,
  current,
  disabled,
  onChange,
  onFocus,
}: {
  groupId: string;
  number: number;
  answer: AnswerState | undefined;
  /** The bank entry the answer names, for showing what was dropped. */
  option: BankOption | undefined;
  /** True when the bank is numbered i, ii, iii rather than lettered. */
  roman?: boolean;
  /** The words shown in an empty gap. Null shows the question's number. */
  placeholder: string | null;
  /** Full width, as the heading slots above each paragraph are. */
  block?: boolean;
  current: boolean;
  disabled: boolean;
  onChange: (patch: Partial<AnswerState>) => void;
  onFocus: () => void;
}) {
  const { pick, setPick } = useDragPick();
  const [over, setOver] = useState(false);
  const value = answer?.value ?? '';

  const place = (letter: string) => {
    onChange({ value: letter });
    setPick(null);
  };

  // A card belongs to the group it came from. Without this a heading picked up
  // for questions 27-32 could be dropped into a summary blank in the same
  // pane, recording a letter that group's key knows nothing about.
  const held = pick?.groupId === groupId ? pick.letter : null;

  // Clicking does what the candidate means by it: with a card held, drop it;
  // with nothing held, take back whatever is in the blank.
  const activate = () => {
    if (disabled) return;
    onFocus();
    if (held) place(held);
    else if (value) onChange({ value: '' });
  };

  return (
    <span
      className={`xdrop${block ? ' xdrop--block' : ''}${current ? ' xdrop--current' : ''}`}
      role="button"
      aria-label={`Question ${number}`}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      data-over={over || undefined}
      data-filled={value ? true : undefined}
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (disabled) return;
        e.preventDefault();
        setOver(false);
        // Only a card this group handed out is accepted. Dragged text from
        // anywhere else — a selection in the passage, another group's bank —
        // would otherwise be recorded as an answer.
        if (held) {
          onFocus();
          place(held);
        }
      }}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
        if ((e.key === 'Backspace' || e.key === 'Delete') && value) {
          e.preventDefault();
          onChange({ value: '' });
        }
      }}
    >
      {placeholder !== null && <span className="xnum">{number}</span>}
      {value ? (
        <span className="xdrop__value">
          {placeholder !== null && (
            <span className="xdrop__letter">{displayLetter(value, roman)}</span>
          )}
          {option?.text ? <span>{option.text}</span> : null}
        </span>
      ) : placeholder === null ? (
        // The real screen leaves the number in the gap and says nothing else:
        // the words that belong there are in the cards beside it.
        <span className="xdrop__number">{number}</span>
      ) : (
        <span className="xdrop__hint">{placeholder}</span>
      )}
    </span>
  );
}
